<script setup lang="ts">
import { computed } from 'vue';
import { zoneIconFor } from '@/composables/useZoneIcon';
import { readNestedDisplayString } from '@/utils/nestedRelation';

/**
 * A zone in a zone picker, drawn the way search results draw it: the zone's mark, its name,
 * and its location's name under it. The row is the zone's search document, which carries the
 * mark's facts and `location.display_string`.
 *
 * `singleLine` puts the location after the name instead of under it, for a picker's button,
 * which has room for one line.
 */
const props = withDefaults(
  defineProps<{
    row: Record<string, unknown>;
    label: string;
    singleLine?: boolean;
  }>(),
  { singleLine: false },
);

const icon = computed(() => zoneIconFor(props.row));
const locationName = computed(() => readNestedDisplayString(props.row.location));
</script>

<template>
  <span class="flex min-w-0 items-center gap-2.5" data-testid="zone-search-option">
    <component :is="icon" class="size-4 shrink-0" aria-hidden="true" />
    <span v-if="singleLine" class="min-w-0 truncate">
      {{ label }}<span v-if="locationName" class="text-muted-foreground" data-testid="zone-search-option-location"> · {{ locationName }}</span>
    </span>
    <span v-else class="flex min-w-0 flex-col">
      <span class="truncate">{{ label }}</span>
      <span v-if="locationName" class="truncate text-xs text-muted-foreground" data-testid="zone-search-option-location">{{ locationName }}</span>
    </span>
  </span>
</template>
